import React, { useMemo, useState } from 'react';
import { Buildings, MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAgency } from '../context/AgencyContext';
import { AgencyFormDialog, PAYMENT_METHODS } from '../components/agencies/AgencyFormDialog';
import { PendingSignupsCard, ViewAgencyDialog } from '../components/agency/AgencyDialogs';
import { StatsCardGrid } from '../components/shared/StatsCardGrid';
import AgenciesTable, { StatusBadge } from './agencies/AgenciesTable';
import PurgeAgencyDialog from './agencies/PurgeAgencyDialog';
import { STATUS_OPTIONS, useAgencies } from './agencies/useAgencies';

const AgencyManagementPage = () => {
  const { isAzaiStaff, azaiRole, fetchAgencies } = useAgency();
  const agencies = useAgencies({ refetchAgenciesContext: fetchAgencies });
  const [purgeTarget, setPurgeTarget] = useState(null);

  const canCreate = azaiRole === 'super_admin' || azaiRole === 'billing_manager';
  const canDelete = azaiRole === 'super_admin';
  const canPurge = azaiRole === 'super_admin';

  const statsCards = useMemo(() => [
    { label: 'Total Agencies', value: agencies.agencies.length, color: 'slate', testid: 'agency-stat-total' },
    { label: 'Active', value: agencies.agencies.filter((a) => a.status === 'active').length, color: 'green', testid: 'agency-stat-active' },
    { label: 'With Medicaid', value: agencies.agencies.filter((a) => a.has_medicaid).length, color: 'blue', testid: 'agency-stat-medicaid' },
    { label: 'With Sandata', value: agencies.agencies.filter((a) => a.has_sandata).length, color: 'indigo', testid: 'agency-stat-sandata' },
    { label: 'With BH', value: agencies.agencies.filter((a) => a.has_bh).length, color: 'amber', testid: 'agency-stat-bh' },
  ], [agencies.agencies]);

  if (!isAzaiStaff) {
    return (
      <div className="p-8 text-center">
        <Buildings size={48} className="mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Only AZAI staff can access agency management.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agency-management-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Agency Management
          </h1>
          <p className="text-slate-500 mt-1">
            Manage client agencies and their billing configurations
          </p>
        </div>
        {canCreate && (
          <Button onClick={agencies.openCreate} data-testid="create-agency-btn">
            <Plus size={18} className="mr-2" />
            New Agency
          </Button>
        )}
      </div>

      <StatsCardGrid columns={5} cards={statsCards} />

      <PendingSignupsCard
        pendingSignups={agencies.pendingSignups}
        onApprove={agencies.approveSignup}
        onReject={agencies.rejectSignup}
      />

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by name, NPI, or email..."
                value={agencies.searchTerm}
                onChange={(e) => agencies.setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="agency-search"
              />
            </div>
            <Select value={agencies.statusFilter} onValueChange={agencies.setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <AgenciesTable
        agencies={agencies.agencies}
        loading={agencies.loading}
        canDelete={canDelete}
        canPurge={canPurge}
        onView={agencies.openView}
        onEdit={agencies.openEdit}
        onDelete={agencies.handleDelete}
        onPurge={setPurgeTarget}
      />

      <PurgeAgencyDialog
        open={!!purgeTarget}
        agency={purgeTarget}
        onClose={() => setPurgeTarget(null)}
        onPurged={() => {
          setPurgeTarget(null);
          agencies.reload();
        }}
      />

      <AgencyFormDialog
        open={agencies.createOpen}
        onOpenChange={agencies.setCreateOpen}
        formData={agencies.formData}
        setFormData={agencies.setFormData}
        onSubmit={agencies.handleCreate}
        mode="create"
      />

      <ViewAgencyDialog
        open={agencies.viewOpen}
        onOpenChange={agencies.setViewOpen}
        agency={agencies.selectedAgency}
        paymentMethods={PAYMENT_METHODS}
        getStatusBadge={(s) => <StatusBadge status={s} />}
      />

      <AgencyFormDialog
        open={agencies.editOpen}
        onOpenChange={agencies.setEditOpen}
        formData={agencies.formData}
        setFormData={agencies.setFormData}
        onSubmit={agencies.handleUpdate}
        mode="edit"
      />
    </div>
  );
};

export default AgencyManagementPage;
